import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Mail, Phone, MapPin, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { faqs } from '../data/mock';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    spaceType: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Envoyer au backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'envoi du formulaire');
      }

      const data = await response.json();
      console.log('Contact form submitted successfully:', data);

      // Show success message
      toast.success('Message envoyé avec succès !', {
        description: 'Notre équipe vous contactera dans les plus brefs délais.',
        duration: 5000
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        spaceType: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting contact form:', error);
      toast.error('Erreur lors de l\'envoi', {
        description: 'Une erreur est survenue. Veuillez réessayer ou nous contacter directement.',
        duration: 5000
      });
    }
  };

  return (
    <section id="contact" className="py-16 md:py-24 bg-gradient-to-b from-white to-[#F8F8F5]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-5xl font-bold text-[#4A6B96] mb-6">
            Contactez-Nous
          </h2>
          <p className="text-base md:text-xl text-gray-600">
            Prêt à découvrir votre futur espace de travail ? Parlons-en ensemble.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* Contact Form */}
          <div className="bg-[#F8F8F5] rounded-2xl shadow-xl p-8 md:p-10">
            <h3 className="text-2xl font-bold text-[#4A6B96] mb-6">
              Réservez Votre Visite
            </h3>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="text-sm font-medium text-gray-700 mb-2">
                  Nom complet *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Jean Dupont"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full" />

              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email" className="text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="jean@exemple.fr"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full" />

                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full" />

                </div>
              </div>

              <div>
                <Label htmlFor="company" className="text-sm font-medium text-gray-700 mb-2">
                  Entreprise
                </Label>
                <Input
                  id="company"
                  name="company"
                  type="text"
                  placeholder="Nom de votre entreprise"
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full" />

              </div>

              <div>
                <Label htmlFor="spaceType" className="text-sm font-medium text-gray-700 mb-2">
                  Type d'espace recherché *
                </Label>
                <Select
                  value={formData.spaceType}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, spaceType: value }))}
                  required>

                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choisissez un type d'espace" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="coworking">Bureau Privé</SelectItem>
                    <SelectItem value="salle-reunion">Salle de Réunion</SelectItem>
                    <SelectItem value="seminaire">Séminaire / Manoir</SelectItem>
                    <SelectItem value="autre">Autre / À discuter</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="message" className="text-sm font-medium text-gray-700 mb-2">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  placeholder="Parlez-nous de votre projet..."
                  value={formData.message}
                  onChange={handleInputChange}
                  rows={4}
                  className="w-full" />

              </div>

              <Button
                type="submit"
                className="w-full bg-[#FFFFFF] hover:bg-[#E5E7EB] text-[#4A6B96] font-semibold text-lg py-6">

                Envoyer ma Demande
              </Button>

              <p className="text-xs text-gray-500 text-center">
                * Champs obligatoires - Vos données sont protégées et ne seront jamais partagées.
              </p>
            </form>
          </div>

          {/* Contact Info & FAQ */}
          <div className="space-y-8">
            {/* Contact Cards */}
            <div className="space-y-4">
              <div className="bg-[#F8F8F5] rounded-xl shadow-lg p-6 border-l-4 border-[#FFFFFF]">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#4A6B96] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Phone className="text-white font-bold" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#4A6B96] mb-1">Téléphone</h4>
                    <p className="text-gray-600">01 71 42 72 15</p>
                    <p className="text-sm text-gray-500 mt-1">Lun-Ven : 9h-18h</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F8F8F5] rounded-xl shadow-lg p-6 border-l-4 border-[#FFFFFF]">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#4A6B96] rounded-xl flex items-center justify-center flex-shrink-0">
                    <Mail className="text-white font-bold" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#4A6B96] mb-1">Email</h4>
                    <p className="text-gray-600">contact@espritouest.com</p>
                    <p className="text-sm text-gray-500 mt-1">Réponse sous 24h</p>
                  </div>
                </div>
              </div>

              <div className="bg-[#F8F8F5] rounded-xl shadow-lg p-6 border-l-4 border-[#FFFFFF]">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-[#4A6B96] rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-white font-bold" size={24} />
                  </div>
                  <div>
                    <h4 className="font-bold text-[#4A6B96] mb-1">Siège Social</h4>
                    <p className="text-gray-600">98 Boulevard de la Reine</p>
                    <p className="text-gray-600">78000 Versailles, France</p>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ Accordion */}
            <div className="bg-[#F8F8F5] rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-bold text-[#4A6B96] mb-6">
                Questions Fréquentes
              </h3>
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq) =>
                <AccordionItem key={faq.id} value={`item-${faq.id}`}>
                    <AccordionTrigger className="text-left font-semibold text-[#4A6B96] hover:text-white font-bold">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </div>
          </div>
        </div>

        {/* CTA Banner */}
        <div className="premium-blue rounded-2xl p-12 text-center text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-[#FFFFFF]/15 blur-3xl pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-[#7B9BC0]/15 blur-3xl pointer-events-none"></div>
          <div className="relative">
            <CheckCircle className="text-white font-bold mx-auto mb-6" size={48} />
            <h3 className="text-3xl font-bold mb-4">
              Visite Sans Engagement
            </h3>
            <p className="text-xl text-gray-200 max-w-2xl mx-auto">
              Découvrez nos espaces, rencontrez nos équipes et imaginez votre futur bureau. 
              C'est gratuit et sans obligation !
            </p>
          </div>
        </div>
      </div>
    </section>);

};

export default Contact;